import { useState } from 'react'
import type { Campaign } from '@/api/client'
import { Button, Drawer, ErrorState, useToast } from '@/ui'
import { AffiliateFields, affiliateDraftOf, affiliateError, affiliatePayload } from './AffiliateFields'
import { useCampaignMutations } from './shared'

/** The 쿠팡 글감 form of a Coupang campaign: what it writes about and how.
 *  Pacing and the account are in the pacing form. A drawer over the campaign
 *  page, mounted only while open (`key={campaign.ID}`), so each opening
 *  starts from the saved settings; it closes itself once saved. */
export function AffiliateEditor({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const toast = useToast()
  const { affiliate } = useCampaignMutations(campaign.ID)
  const [draft, setDraft] = useState(() => affiliateDraftOf(campaign.Affiliate))
  const [problem, setProblem] = useState<string | null>(null)
  const targetId = campaign.PlatformConfigs?.threads?.target_id ?? 0

  const save = async () => {
    const found = affiliateError(draft, targetId)
    setProblem(found)
    if (found) return
    try {
      await affiliate.mutateAsync({ id: campaign.ID, affiliate: affiliatePayload(draft) })
    } catch {
      return // shown below; the form keeps what was typed
    }
    toast.success('쿠팡 설정을 저장했다')
    onClose()
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title="쿠팡 글감 편집"
      meta={campaign.Name}
      footer={
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" loading={affiliate.isPending} onClick={() => void save()}>
            쿠팡 설정 저장
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-table text-muted-foreground">
          고른 카테고리에서 아직 쓰지 않은 가장 높은 점수의 시그널로 글을 쓴다. 광고성 심사는 거치지 않는다.
        </p>
        <AffiliateFields
          value={draft}
          onChange={(next) => {
            setProblem(null)
            setDraft(next)
          }}
        />
        {problem != null && (
          <p role="alert" className="text-table text-status-failed">
            {problem}
          </p>
        )}
        {problem == null && affiliate.error != null && <ErrorState title="저장하지 못했다" error={affiliate.error} />}
      </div>
    </Drawer>
  )
}
